import { isEqual } from 'lodash'
import {secondsDiff} from '@/utils/datetime'

export const mutations = {
  SET_FEATURES(state, payload) {
    const { features, isMerge } = payload

    const newFeatures = features.features.map(f => {
      const { resources } = f.properties
      // Add thumbUrl to feature for easier access in layer API
      const thumbUrl = resources?.find(r => r.url.includes('small'))?.url || resources?.find(r => r.url.includes('thumbnail'))?.url
      if (thumbUrl) {
        f.properties.thumbUrl = thumbUrl
      }
      return f
    })

    state.features = {
      type: 'FeatureCollection',
      features: newFeatures,
    }
    
    // Automatically hide project boundaries by default
    if (newFeatures) {
      newFeatures.forEach(feature => {
        if (feature.properties?.paired_project_id != null) {
          const code = feature.properties.code
          // Only set to false if not already set
          if (state.featureVisibility[code] === undefined) {
            state.featureVisibility = { ...state.featureVisibility, [code]: false }
          }
        }
      })
    }
  },

  SET_FEED_FEATURES(state, payload) {
    const { features, isMerge } = payload

    state.feedFeatures = {
      type: 'FeatureCollection',
      features: features.features
    }
    
    // Automatically hide project boundaries by default
    if (features.features) {
      features.features.forEach(feature => {
        if (feature.properties?.paired_project_id != null) {
          const code = feature.properties.code
          // Only set to false if not already set
          if (state.featureVisibility[code] === undefined) {
            state.featureVisibility = { ...state.featureVisibility, [code]: false }
          }
        }
      })
    }
  },

  DELETE_FEATURE (state, code) {
    state.features = {
      type: 'FeatureCollection',
      features: state.features.features.filter(f => f.properties.code !== code)
    }

    state.feedFeatures = {
      type: 'FeatureCollection',
      features: state.feedFeatures.features.filter(f => f.properties.code !== code)
    }
  },

  SET_CURRENT_FEATURE  (state, payload) {
    state.currentFeature = payload.feature
  },
  SET_CURRENT_FEATURES_CHILDS(state, payload) {
    state.currentFeatureChilds = payload
  },

  CLEAR_SEARCH_SELECTED(state) {
    state.searchSelected = []
  },

  SET_MAP(state, payload) {
    state.map = payload
  },
  SET_MAP2(state, payload) {
    state.map2 = payload
  },
  SET_COMPARE_MODE(state, payload) {
    state.isCompareMode = payload
  },
  SET_MAP_READY(state, payload) {
    state.mapReady = payload
  },
  SET_MAP2_READY(state, payload) {
    state.map2Ready = payload
  },
  SET_DRAW(state, payload) {
    state.draw = payload
  },

  SET_BEACONS(state, payload) {
    state.beacons = payload
  },
  NEXT_BEACON(state, next_beacon) {
    if (!state.beacons || state.beacons.find(b => b.properties.member_id === next_beacon.properties.member_id)) {
      const index = state.beacons.findIndex(b => b.properties.member_id === next_beacon.properties.member_id)
      state.beacons.splice(index, 1, next_beacon)
    } else {
      state.beacons.push(next_beacon)
    }
  },
  REMOVE_BEACON(state, memberId) {
    const index = state.beacons.findIndex(beacon => beacon.properties.member_id === memberId)
    state.beacons.splice(index, 1)
  },
  CLEANUP_BEACONS(state) {
    const day = 24 * 60 * 60 * 1000
    state.beacons = state.beacons.filter(beacon => secondsDiff(new Date(), beacon.properties.rel_ts) <= day)
    console.log('WS: cleanup beacons')
  },

  SET_NOTES(state, payload) {
    state.notes = payload
  },
  ADD_NOTE(state, payload) {
    state.notes.push(payload)
  },
  DELETE_NOTE(state, noteId) {
    state.notes = state.notes.filter(n => n.id !== noteId)
  },

  SET_LOCATION(state, location) {
    if(location === null) {
      state.userSettings.location.lat = null
      state.userSettings.location.lng = null
    } else {
      state.userSettings.location.lat = location.coords.latitude
      state.userSettings.location.lng = location.coords.longitude
    }
  },

  SET_LAYERS(state, payload) {
    state.layers = payload
  },

  SET_FILES (state, payload) {
    state.files = payload
  },

  SET_TOTAL_ONMAP(state, payload) {
    state.totalOnMap = payload
  },

  ADD_REPORT_TEMPLATE(state, payload) {
    state.reportTemplates.push(payload)
  },

  SET_REPORT_TEMPLATES(state, payload) {
    state.reportTemplates = payload
  },

  SET_ALL_TEMPLATES(state, payload) {
    state.allTemplates = payload
  },

  UPDATE_REPORT_TEMPLATE(state, payload) {
    const index = state.reportTemplates.findIndex(t => t.id === payload.id)
    state.reportTemplates.splice(index, 1, payload)
  },

  DELETE_REPORT_TEMPLATE(state, payload) {
    state.reportTemplates = state.reportTemplates.filter(t => t.id !== payload.id)
  },

  SET_FEATURE_CREATED(state, payload) {
    state.isFeatureCreated = payload
  },

  SET_PROJECT_BOUNDARIES_VISIBLE(state, payload) {
    state.projectBoundariesVisible = payload
  },

  TOGGLE_FEATURE_VISIBILITY(state, featureCode) {
    if (state.featureVisibility[featureCode] === undefined || state.featureVisibility[featureCode] === true) {
      // If not set or visible, hide it
      state.featureVisibility = { ...state.featureVisibility, [featureCode]: false }
    } else {
      // If hidden, show it
      state.featureVisibility = { ...state.featureVisibility, [featureCode]: true }
    }
  },

  SET_FEATURE_VISIBILITY(state, { featureCode, visible }) {
    state.featureVisibility = { ...state.featureVisibility, [featureCode]: visible }
  },

  RESET_FEATURE_VISIBILITY(state) {
    state.featureVisibility = {}
  },

  SET_RESOURCES(state, resources) {
    state.resources = resources
  },

  ADD_RESOURCE(state, resource) {
    if (!state.resources) {
      state.resources = []
    }
    state.resources.push(resource)
  },

  REMOVE_RESOURCE(state, resourceId) {
    if (state.resources) {
      state.resources = state.resources.filter(r => !(r.uuid === resourceId || r.id === resourceId))
    }
  },

  UPDATE_RESOURCE(state, { resourceId, updates }) {
    if (state.resources) {
      const index = state.resources.findIndex(r => r.uuid === resourceId || r.id === resourceId)
      if (index !== -1) {
        state.resources[index] = { ...state.resources[index], ...updates }
      }
    }
  }
}
